import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import { API_BASE_URL } from '../config';
import { RESCUE_RECIPES, getRescueRecipe } from '../data/rescueRecipes';
import TransformationCard from '../components/TransformationCard';
import WasteConfirmationModal from '../components/WasteConfirmationModal';
import SuccessModal from '../components/SuccessModal';
import useBabyPrompt from '../hooks/useBabyPrompt';
import BreakfastRecommendationModal from '../components/BreakfastRecommendationModal';
import SchoolChildModal from '../components/SchoolChildModal';
import { registerWebPushSubscription, testSelfWebPush, getServerPushStatus, detectCurrentPushDeviceKind } from '../lib/push/webPushClient';
import { repairMojibake } from '../utils/textRepair';

const HIDDEN_BREAKFAST_NOTIFICATION_TYPES = new Set([
    'IKM_BREAKFAST_BABY',
    'IKM_BREAKFAST_HELLO',
    'IKM_BREAKFAST_RISK',
    'IKM_BREAKFAST_RECALL'
]);

const LEGACY_UI_TRIGGER_TYPES = new Set([
    'IKM_AUTO_MENU_ONBOARDING',
    'IKM_DAY7_MILESTONE'
]);

export default function NotificationsPage({ isDarkMode, toggleDarkMode }) {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAllByDefault, setShowAllByDefault] = useState(() => {
        try {
            return localStorage.getItem('notifications_show_all_default') === 'true';
        } catch (e) {
            return false;
        }
    });
    const [activeTab, setActiveTab] = useState(() => {
        try {
            return localStorage.getItem('notifications_show_all_default') === 'true' ? 'ALL' : 'BROADCAST';
        } catch (e) {
            return 'BROADCAST';
        }
    }); // BROADCAST, IKM_ALERT, IKM_AUDIT, IKM_TRANSFORM, ALL

    // --- Food Rescue & Waste State ---
    const [transformationData, setTransformationData] = useState(null); // { recipe, sourceItem }
    const [wasteData, setWasteData] = useState(null); // { itemName, notifId }
    const [feedbackModal, setFeedbackModal] = useState({ isOpen: false, title: '', message: '', type: 'success' });
    const [breakfastModalOpen, setBreakfastModalOpen] = useState(false);
    const [rescueSelectionModal, setRescueSelectionModal] = useState({ isOpen: false, transformations: [], notifId: null, showLunchboxOnboarding: false });
    // [Çoklu tik-kurtar] seçili tarif key'leri + batch işlem kilidi (yarış/çift-işlem önler)
    const [rescueSelected, setRescueSelected] = useState(() => new Set());
    const [rescueProcessing, setRescueProcessing] = useState(false);
    const [expiredReviewModal, setExpiredReviewModal] = useState({ isOpen: false, notification: null, selectedBatchIds: [] });
    const [schoolLunchModal, setSchoolLunchModal] = useState({ isOpen: false, notifId: null, pendingAction: null });
    const [pushBusy, setPushBusy] = useState(false);
    const [pushPermission, setPushPermission] = useState('default');
    const [pushStatus, setPushStatus] = useState('permission_default');
    const [awaitingPermissionFix, setAwaitingPermissionFix] = useState(false);
    const [mobilePushReminder, setMobilePushReminder] = useState(null);
    const [completedBreakfastNotificationId, setCompletedBreakfastNotificationId] = useState(null);

    // --- baby profile ---
    const { profile } = useBabyPrompt();
    const babyName = profile?.babyName || 'Bebeğiniz';
    const currentPushDeviceKind = detectCurrentPushDeviceKind();
    const isMobilePushRuntime = currentPushDeviceKind === 'mobile_web';

    const navigate = useNavigate();
    const location = useLocation();
    const autoRescueHandledRef = React.useRef(false);

    const formatQuantityUnit = (quantity, unit) => {
        const n = Number(quantity || 0);
        const display = Number.isInteger(n) ? String(n) : n.toLocaleString('tr-TR', { maximumFractionDigits: 2 });
        return `${display} ${unit || 'adet'}`.trim();
    };

    const formatExpiredReviewItemLabel = (item) => {
        const name = repairMojibake(item?.name || 'Ürün');
        return `${name} (${formatQuantityUnit(item?.quantity, item?.unit)})`;
    };

    const getExpiredReviewItems = (notif) => {
        const items = Array.isArray(notif?.targetFilters?.items) ? notif.targetFilters.items : [];
        return items.filter((item) => item && item.batchId);
    };

    const fetchNotifications = async () => {
        try {
            const authToken = localStorage.getItem('authToken');
            const res = await fetch(`${API_BASE_URL}/api/notifications`, {
                headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
            });
            if (res.ok) {
                const data = await res.json();
                setNotifications((data.notifications || []).filter((notif) => !HIDDEN_BREAKFAST_NOTIFICATION_TYPES.has(String(notif?.type || '').toUpperCase())));
            }
        } catch (err) {
            console.error('Fetch notifications error:', err);
        } finally {
            setLoading(false);
        }
    };

    const refreshPushState = async ({ allowSilentRepair = true } = {}) => {
        try {
            if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
                setPushPermission('unsupported');
                setPushStatus('unsupported');
                return;
            }
            const permission = Notification.permission || 'default';
            setPushPermission(permission);
            if (permission === 'denied') {
                setPushStatus('permission_denied');
                return;
            }
            if (permission !== 'granted') {
                setPushStatus('permission_default');
                return;
            }
            const reg = await navigator.serviceWorker.getRegistration('/push-sw.js')
                || await navigator.serviceWorker.getRegistration();
            if (!reg) {
                setPushStatus('no_browser_subscription');
                return;
            }
            const sub = await reg.pushManager.getSubscription();
            if (!sub) {
                setPushStatus('no_browser_subscription');
                return;
            }

            const serverStatus = await getServerPushStatus();
            if (
                isMobilePushRuntime &&
                serverStatus?.ok &&
                serverStatus?.hasDesktopWebSubscription &&
                !serverStatus?.hasMobileWebSubscription
            ) {
                setMobilePushReminder({
                    variant: 'needs_mobile_opt_in',
                    hasDesktopWebSubscription: true
                });
            } else {
                setMobilePushReminder(null);
            }

            if (isMobilePushRuntime && serverStatus?.ok && !serverStatus?.hasMobileWebSubscription) {
                if (!sub) {
                    setPushStatus('no_browser_subscription');
                    return;
                }

                if (allowSilentRepair) {
                    setPushStatus('repairing');
                    const subscribed = await registerWebPushSubscription({ requestPermission: false, forceRefresh: false });
                    if (subscribed?.ok) {
                        const repairedStatus = await getServerPushStatus();
                        if (repairedStatus?.ok && repairedStatus?.hasMobileWebSubscription) {
                            setMobilePushReminder(null);
                            setPushStatus('active');
                            return;
                        }
                    }
                }

                setPushStatus('browser_active_server_missing');
                return;
            }

            if (serverStatus?.ok && Number(serverStatus.activeCount || 0) > 0) {
                setPushStatus('active');
                return;
            }

            if (allowSilentRepair) {
                setPushStatus('repairing');
                const subscribed = await registerWebPushSubscription({ requestPermission: false, forceRefresh: false });
                if (subscribed?.ok) {
                    const repairedStatus = await getServerPushStatus();
                    if (repairedStatus?.ok && Number(repairedStatus.activeCount || 0) > 0) {
                        setPushStatus('active');
                        return;
                    }
                }
            }

            setPushStatus('browser_active_server_missing');
        } catch (_) {
            setMobilePushReminder(null);
            setPushStatus('error');
        }
    };

    useEffect(() => {
        fetchNotifications();
        refreshPushState();

        const handleRefresh = () => fetchNotifications();
        window.addEventListener('refreshNotifications', handleRefresh);

        // Also listen to BroadcastChannel for cross-tab updates
        const bc = new BroadcastChannel('notifications_channel');
        bc.onmessage = (event) => {
            if (event.data === 'refresh') fetchNotifications();
        };

        return () => {
            window.removeEventListener('refreshNotifications', handleRefresh);
            bc.close();
        };
    }, []);

    // [Dashboard rescue nudge] /notifications'a autoOpenRescue ile gelindiyse ilk kurtarma
    // bildiriminin kurtarma seçim modalını otomatik aç (kullanıcı aramasın — aynı modal).
    useEffect(() => {
        if (!location.state?.autoOpenRescue || autoRescueHandledRef.current) return;
        if (!Array.isArray(notifications) || notifications.length === 0) return;
        const rescueNotif = notifications.find((n) => {
            if (n?.isRead) return false;
            const tf = Array.isArray(n?.targetFilters?.transformations) ? n.targetFilters.transformations : [];
            return tf.length > 0;
        });
        if (rescueNotif) {
            autoRescueHandledRef.current = true;
            setRescueSelected(new Set());
            setRescueSelectionModal({
                isOpen: true,
                transformations: rescueNotif.targetFilters.transformations,
                notifId: rescueNotif.id,
                showLunchboxOnboarding: !!rescueNotif.targetFilters?.showLunchboxOnboarding
            });
            // state'i temizle ki geri/yenilemede tekrar açılmasın
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, notifications, navigate, location.pathname]);

    useEffect(() => {
        const handleVisibilityRefresh = () => {
            if (document.visibilityState === 'visible' && !awaitingPermissionFix) {
                refreshPushState({ allowSilentRepair: true });
            }
        };

        const handleWindowFocus = () => {
            if (!awaitingPermissionFix) {
                refreshPushState({ allowSilentRepair: true });
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityRefresh);
        window.addEventListener('focus', handleWindowFocus);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityRefresh);
            window.removeEventListener('focus', handleWindowFocus);
        };
    }, [awaitingPermissionFix]);

    const markAsRead = async (id, reason = 'viewed') => {
        try {
            const authToken = localStorage.getItem('authToken');
            await fetch(`${API_BASE_URL}/api/notifications/${id}/read`, {
                method: 'POST',
                headers: {
                    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ reason })
            });
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
            window.dispatchEvent(new CustomEvent('refreshNotifications'));
        } catch (err) {
            console.error('Mark as read error:', err);
        }
    };

    const handleNotificationClick = async (notif) => {
        const isBreakfastIntent = (
            notif?.type === 'BREAKFAST_INTENT'
            || notif?.targetFilters?.action === 'open_breakfast_blue_modal'
        );
        const shoppingListId = Number(notif?.targetFilters?.listId || 0);
        const isShoppingReviewNotification = (
            notif?.type === 'SHOPPING_COMPLETED'
            || notif?.targetFilters?.view === 'shopping_history'
            || notif?.targetFilters?.open_review === true
        );

        if (isBreakfastIntent) {
            handleOpenBreakfastModal(notif);
            return;
        }

        if (!notif.isRead && notif.type !== 'IKM_TRANSFORM') {
            await markAsRead(notif.id);
        }

        if (isShoppingReviewNotification && shoppingListId > 0) {
            navigate('/my-shopping-lists', {
                state: {
                    openHistory: true,
                    openListId: shoppingListId,
                    autoOpenReview: true,
                    source: 'shopping_completed_notification'
                }
            });
            return;
        }

        if (notif.targetFilters?.route) {
            if (notif.targetFilters.route === '/my-shopping-lists' && notif.targetFilters.openSmartList) {
                navigate('/my-shopping-lists', {
                    state: {
                        openSmartList: true,
                        source: notif.targetFilters.source || 'notification'
                    }
                });
                return;
            }
            if (notif.targetFilters.openPremiumPurchase) {
                navigate(notif.targetFilters.route, {
                    state: {
                        openPremiumPurchase: true,
                        paymentIntentId: notif.targetFilters.paymentIntentId || null,
                        paymentModuleKey: notif.targetFilters.paymentModuleKey || null
                    }
                });
                return;
            }

            navigate(notif.targetFilters.route, {
                state: {
                    prefill: {
                        eventType: notif.targetFilters.eventType || 'birthday',
                        entrySource: notif.targetFilters.entrySource || 'manual',
                        healthCheckEnabled: !!notif.targetFilters.healthCheckEnabled
                    }
                }
            });
            return;
        }

        if (notif.targetFilters?.view === 'shopping_history') {
            navigate('/my-shopping-lists', {
                state: {
                    openHistory: true,
                    openListId: notif.targetFilters.listId,
                    autoOpenReview: notif.targetFilters.open_review === true
                }
            });
        }
        // Add other view navigations if needed
    };

    const markAllRead = async () => {
        try {
            const authToken = localStorage.getItem('authToken');
            await fetch(`${API_BASE_URL}/api/notifications/mark-all-read`, {
                method: 'POST',
                headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
            });
            setNotifications(prev => prev.map(n => (
                n.type === 'IKM_TRANSFORM' ? n : { ...n, isRead: true }
            )));
            window.dispatchEvent(new CustomEvent('refreshNotifications'));
        } catch (err) {
            console.error('Mark all read error:', err);
        }
    };

    useEffect(() => {
        if (!completedBreakfastNotificationId) return;
        const exists = notifications.some((notif) => notif.id === completedBreakfastNotificationId);
        if (!exists) {
            setCompletedBreakfastNotificationId(null);
        }
    }, [notifications, completedBreakfastNotificationId]);

    const handleEnableWebPush = async () => {
        try {
            setPushBusy(true);
            setPushStatus('repairing');
            if ('Notification' in window && Notification.permission === 'denied') {
                setAwaitingPermissionFix(true);
                setPushStatus('permission_denied');
                setFeedbackModal({
                    isOpen: true,
                    type: 'warning',
                    title: 'Bildirim izni kapalı',
                    message: 'Kilit ikonuna basıp Bildirimler > İzin ver yap. Uygulamaya geri dönünce otomatik tamamlayacağız.'
                });
                return;
            }

            const subscribed = await registerWebPushSubscription({ requestPermission: true, forceRefresh: true });
            if (!subscribed?.ok) {
                const reasonText = subscribed?.reason === 'permission_not_granted' || subscribed?.reason === 'permission_denied'
                    ? 'Bildirim izni verilmedi. Kilit ikonundan Bildirimler > İzin ver yapın. Geri dönünce otomatik tamamlayacağız.'
                    : `Neden: ${subscribed?.reason || 'bilinmiyor'}`;
                if (subscribed?.reason === 'permission_not_granted' || subscribed?.reason === 'permission_denied') {
                    setAwaitingPermissionFix(true);
                }
                setFeedbackModal({
                    isOpen: true,
                    type: 'error',
                    title: 'Web Push açılamadı',
                    message: reasonText
                });
                setPushStatus(subscribed?.reason === 'permission_not_granted' || subscribed?.reason === 'permission_denied' ? 'permission_denied' : 'error');
                return;
            }

            const smoke = await testSelfWebPush();
            const sent = Number(smoke?.result?.sent || 0);
            setAwaitingPermissionFix(false);
            await refreshPushState();
            setFeedbackModal({
                isOpen: true,
                type: sent > 0 ? 'success' : 'warning',
                title: sent > 0 ? 'Web Push aktif' : 'Abonelik tamam',
                message: sent > 0
                    ? 'Test bildirimi gönderildi. Telefon/masaüstünde bildirim merkezini kontrol edin.'
                    : 'Abonelik kaydedildi. Test gönderiminde aktif cihaz bulunamadı.'
            });
        } catch (err) {
            setPushStatus('error');
            setFeedbackModal({
                isOpen: true,
                type: 'error',
                title: 'Web Push hatası',
                message: err?.message || 'Beklenmeyen hata'
            });
        } finally {
            setPushBusy(false);
        }
    };

    useEffect(() => {
        if (!awaitingPermissionFix) return undefined;

        let busy = false;
        const tryAutoRecover = async () => {
            if (busy) return;
            if (!('Notification' in window)) return;
            if (Notification.permission !== 'granted') return;
            busy = true;
            try {
                setPushBusy(true);
                const subscribed = await registerWebPushSubscription({ requestPermission: false, forceRefresh: false });
                if (subscribed?.ok) {
                    const smoke = await testSelfWebPush();
                    const sent = Number(smoke?.result?.sent || 0);
                    await refreshPushState();
                    setAwaitingPermissionFix(false);
                    setFeedbackModal({
                        isOpen: true,
                        type: sent > 0 ? 'success' : 'warning',
                        title: sent > 0 ? 'Web Push aktif' : 'Abonelik tamam',
                        message: sent > 0
                            ? 'Bildirim izni alındı ve test mesajı gönderildi.'
                            : 'İzin alındı, abonelik yenilendi. Test mesajı şu an cihaza düşmedi.'
                    });
                }
            } finally {
                setPushBusy(false);
                busy = false;
            }
        };

        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                tryAutoRecover();
            }
        };

        window.addEventListener('focus', tryAutoRecover);
        document.addEventListener('visibilitychange', onVisibility);
        tryAutoRecover();

        return () => {
            window.removeEventListener('focus', tryAutoRecover);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [awaitingPermissionFix]);

    const pushUiState = (() => {
        if (pushBusy || pushStatus === 'repairing') {
            return {
                label: 'EŞLEŞTİRİLİYOR',
                className: 'bg-amber-50 dark:bg-amber-900/10 text-amber-700 dark:text-amber-300 border-amber-100 dark:border-amber-900/20',
                disabled: true,
                showPrimary: true,
                showRepair: false
            };
        }
        if (isMobilePushRuntime && mobilePushReminder?.variant === 'needs_mobile_opt_in') {
            return {
                label: 'TELEFONDA BİLDİRİMİ AÇ',
                className: 'bg-sky-50 dark:bg-sky-900/10 text-sky-700 dark:text-sky-300 border-sky-100 dark:border-sky-900/20',
                disabled: false,
                showPrimary: true,
                showRepair: false
            };
        }
        switch (pushStatus) {
            case 'active':
                return {
                    label: 'WEB PUSH AKTİF',
                    className: 'bg-slate-100 dark:bg-slate-800/80 text-slate-500 dark:text-slate-300 border-slate-200 dark:border-slate-700',
                    disabled: true,
                    showPrimary: true,
                    showRepair: false
                };
            case 'permission_denied':
                return {
                    label: 'BİLDİRİM İZNİ KAPALI',
                    className: 'bg-rose-50 dark:bg-rose-900/10 text-rose-700 dark:text-rose-300 border-rose-100 dark:border-rose-900/20',
                    disabled: false,
                    showPrimary: true,
                    showRepair: false
                };
            case 'unsupported':
                return {
                    label: 'WEB PUSH DESTEKLENMİYOR',
                    className: 'bg-slate-100 dark:bg-slate-900/60 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-800',
                    disabled: true,
                    showPrimary: true,
                    showRepair: false
                };
            case 'browser_active_server_missing':
                return {
                    label: 'WEB PUSH EŞLEŞMESİ EKSİK',
                    className: 'bg-orange-50 dark:bg-orange-900/10 text-orange-700 dark:text-orange-300 border-orange-100 dark:border-orange-900/20',
                    disabled: true,
                    showPrimary: true,
                    showRepair: true
                };
            case 'error':
                return {
                    label: 'WEB PUSH DURUMU BELİRSİZ',
                    className: 'bg-orange-50 dark:bg-orange-900/10 text-orange-700 dark:text-orange-300 border-orange-100 dark:border-orange-900/20',
                    disabled: true,
                    showPrimary: true,
                    showRepair: true
                };
            case 'permission_default':
            case 'no_browser_subscription':
            default:
                return {
                    label: 'WEB PUSH AÇ',
                    className: 'bg-emerald-50 dark:bg-emerald-900/10 text-emerald-700 dark:text-emerald-300 border-emerald-100 dark:border-emerald-900/20',
                    disabled: false,
                    showPrimary: true,
                    showRepair: false
                };
        }
    })();

    // --- HELPER: Find Inventory Item by Name (FIFO) ---
    const findInventoryItemByName = async (name) => {
        try {
            const authToken = localStorage.getItem('authToken');
            // Use search parameter and higher limit to ensure we find the item
            const res = await fetch(`${API_BASE_URL}/api/inventory?search=${encodeURIComponent(name)}&limit=100`, {
                headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
            });
            if (res.ok) {
                const data = await res.json();
                const inventory = data.inventory || [];

                // CLIENT-SIDE SAFETY FILTER: Ensure the name is actually a match (fuzzy from backend, strict here)
                const searchLower = name.toLowerCase().trim();
                const matchedItems = inventory.filter(item => {
                    const itemName = (item.ingredient_name || '').toLowerCase();
                    return itemName.includes(searchLower) || searchLower.includes(itemName);
                });

                // Sort by expiry date asc to get the one closest to expiry (FIFO)
                const sorted = matchedItems.sort((a, b) => new Date(a.expiry_date) - new Date(b.expiry_date));

                console.log(`🔍 findInventoryItemByName("${name}"): Found ${sorted.length} matches after filtering.`);
                return sorted.length > 0 ? sorted[0] : null;
            }
        } catch (err) {
            console.error('Find inventory error:', err);
        }
        return null;
    };

    // --- ACTION: HANDLE RESCUE (YES) ---
    const handleRescueClick = async (itemName, notifId, transformId = null, showOnboarding = false, batchId = null) => {
        if (showOnboarding) {
            setSchoolLunchModal({
                isOpen: true,
                notifId: notifId,
                pendingAction: { itemName, notifId, transformId }
            });
            return;
        }

        let recipe = null;

        // 1. Handle Dynamic DB Recipes (e.g. RECIPE:123)
        if (transformId && transformId.startsWith('RECIPE:')) {
            const dishId = transformId.split(':')[1];
            try {
                const authToken = localStorage.getItem('authToken');
                const res = await fetch(`${API_BASE_URL}/api/recipes/id/${dishId}`, {
                    headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data.success && data.recipe) {
                        const dish = data.recipe;

                        // Parse instructions safely
                        let rawSteps = dish.instructions;
                        if (typeof rawSteps === 'string') {
                            try {
                                const parsed = JSON.parse(rawSteps);
                                if (Array.isArray(parsed)) rawSteps = parsed;
                            } catch (e) { }
                        }

                        let steps = [];
                        let babySafetyStep = dish.isChildFriendly ? null : (dish.babyAdjustment || "Bebek tüketimi için uygun olmayabilir.");

                        if (Array.isArray(rawSteps)) {
                            rawSteps.forEach(s => {
                                // Extract baby safety info from dedicated portion_split steps
                                if (s.type === 'portion_split' || s.islem?.toLowerCase().includes('bebek porsiyonu')) {
                                    babySafetyStep = s.detay_acemi || s.detay || s.detay_usta || s.step || s.islem;
                                } else {
                                    // Regular cooking steps: prioritize details by level, fallback to title
                                    const stepText = s.detay_acemi || s.detay || s.detay_usta || s.step || s.islem;
                                    if (stepText) steps.push(stepText);
                                }
                            });
                        }

                        // Map Dish model to Transformation format
                        recipe = {
                            id: dish.id,
                            targetName: dish.name,
                            description: dish.description || "Akıllı mutfak kurtarma operasyonu.",
                            steps: steps.filter(s => s && s.trim().length > 0),
                            newExpiryDays: dish.lockOutDays || 7,
                            extendedShelfLifeDays: dish.lockOutDays || 7, // Used for calculation
                            storageLocation: dish.category === 'MAIN' ? 'Buzdolabı' : (dish.category === 'FILLING' ? 'Buzdolabı' : 'Kiler'),
                            storageInstructions: dish.babyAdjustment || "Uygun bir kapta saklayınız.",
                            unit: 'Adet',
                            mainZone: dish.category === 'MAIN' ? 'Fridge' : (dish.category === 'FILLING' ? 'Fridge' : 'Pantry'),
                            subZone: 'General',
                            estimatedValue: 20,
                            babySafetyStep: babySafetyStep
                        };
                    }
                }
            } catch (err) {
                console.error('Failed to fetch dynamic rescue recipe:', err);
            }
        } else {
            // 2. Fallback to static recipes from rescueRecipes.js or item name (legacy)
            recipe = getRescueRecipe(transformId || itemName);
        }

        if (recipe) {
            const recipeCopy = { ...recipe };

            // Check Baby Safety dynamically for static recipes or if needed
            if (!recipe.id) { // Only for static ones, dynamic ones already have safety info mapped
                try {
                    const res = await fetch(`${API_BASE_URL}/api/safety/check?ingredient=${encodeURIComponent(itemName)}`);
                    if (res.ok) {
                        const safetyData = await res.json();
                        if (!safetyData.safe) {
                            recipeCopy.babySafetyStep = `DİKKAT: ${safetyData.riskMessage} (${safetyData.minAgeMonths}+ Ay)`;
                        }
                    }
                } catch (err) {
                    console.error('Safety check failed', err);
                }
            }

            setTransformationData({
                recipe: recipeCopy,
                sourceItem: itemName,
                notifId: notifId,
                batchId: batchId || null
            });

            // If we have extra metadata in notification filters, merge it
            const notif = notifications.find(n => n.id === notifId);
            if (notif && notif.targetFilters) {
                setTransformationData(prev => ({
                    ...prev,
                    usedRecipeId: notif.targetFilters.usedRecipeId,
                    producedDictionaryId: notif.targetFilters.producedDictionaryId,
                    batchId: prev.batchId || (notif.targetFilters.transformations?.find(t => t.ingredientName === itemName)?.batchId)
                }));
            }
        }
    };

    // --- [Çoklu tik-kurtar] seçim + batch kurtarma ---
    const rescueKeyOf = (t, index) => `${t.ingredientName}::${t.transform_id || index}`;

    const toggleRescueSelect = (key) => {
        setRescueSelected((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const toggleRescueSelectAll = () => {
        setRescueSelected((prev) => {
            const all = rescueSelectionModal.transformations.map((t, i) => rescueKeyOf(t, i));
            // hepsi seçiliyse temizle, değilse hepsini seç
            if (all.every((k) => prev.has(k))) return new Set();
            return new Set(all);
        });
    };

    // Tek bir dönüşümü kurtar (refresh-audit/feedback YOK — batch sonunda TEK sefer yapılır).
    const performOneRescue = async (t) => {
        const authToken = localStorage.getItem('authToken');
        const headers = { 'Content-Type': 'application/json', ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) };

        // Tercih: atomik harvest (server create+deduct'ı atomik yapar, doğru miktar).
        if (t.usedRecipeId) {
            const dishId = String(t.transform_id || '').startsWith('RECIPE:') ? String(t.transform_id).split(':')[1] : undefined;
            const res = await fetch(`${API_BASE_URL}/api/recipes/harvest`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ usedRecipeId: t.usedRecipeId, harvestedPortion: t.harvestedPortion || 1, dishId })
            });
            if (!res.ok) {
                const e = await res.json().catch(() => ({}));
                throw new Error(e.error || 'harvest_failed');
            }
            return;
        }

        // Legacy fallback: statik tarif → yeni stok oluştur + kaynak batch'i sil.
        const recipe = getRescueRecipe(t.transform_id || t.ingredientName);
        if (!recipe) throw new Error('recipe_not_found');
        const newExpiryDate = new Date();
        newExpiryDate.setDate(newExpiryDate.getDate() + (recipe.extendedShelfLifeDays || 7));
        const createRes = await fetch(`${API_BASE_URL}/api/inventory`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                ingredient_name: recipe.targetName,
                dictionaryId: recipe.targetDictionaryId || null,
                purchase_price: recipe.estimatedValue || 0,
                remaining_amount: recipe.producedAmount || 1,
                unit: recipe.producedUnit || recipe.unit || 'Adet',
                expiry_date: newExpiryDate.toISOString().split('T')[0],
                storage_location: recipe.storageLocation || 'Buzdolabı',
                main_zone: recipe.mainZone || ({ 'Kiler': 'Pantry', 'Buzdolabı': 'Fridge', 'Derin Dondurucu': 'Freezer', 'Dondurucu': 'Freezer' }[recipe.storageLocation] || 'Fridge'),
                sub_zone: recipe.subZone || 'General',
                is_rescued: true
            })
        });
        if (!createRes.ok) {
            const e = await createRes.json().catch(() => ({}));
            throw new Error(e.error || 'create_failed');
        }
        if (t.batchId) {
            const delRes = await fetch(`${API_BASE_URL}/api/inventory/${t.batchId}?isTransformation=true`, { method: 'DELETE', headers });
            if (!delRes.ok) {
                const e = await delRes.json().catch(() => ({}));
                throw new Error(e.error || 'delete_failed');
            }
        }
    };

    const handleBatchRescue = async () => {
        if (rescueProcessing) return; // çift-tetik/yarış kilidi
        const { transformations, notifId } = rescueSelectionModal;
        const selected = transformations.filter((t, i) => rescueSelected.has(rescueKeyOf(t, i)));
        if (!selected.length) return;

        setRescueProcessing(true);
        let ok = 0; const failedNames = [];
        for (const t of selected) {
            try {
                await performOneRescue(t); // SIRALI — atomik, yarış yok
                ok += 1;
            } catch (err) {
                console.error('[BatchRescue] item failed:', t.ingredientName, err?.message);
                failedNames.push(t.ingredientName);
            }
        }

        // TEK sefer temizlik (per-item refresh-audit storm'u yerine)
        try {
            const authToken = localStorage.getItem('authToken');
            if (ok > 0 && notifId) await markAsRead(notifId, 'rescue_completed');
            await fetch(`${API_BASE_URL}/api/notifications/refresh-audit`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${authToken}` }
            }).catch(() => { });
        } catch (_) { /* sessiz */ }

        setRescueSelectionModal({ isOpen: false, transformations: [], notifId: null, showLunchboxOnboarding: false });
        setRescueSelected(new Set());
        setRescueProcessing(false);
        fetchNotifications();

        const fail = failedNames.length;
        setFeedbackModal({
            isOpen: true,
            title: fail === 0 ? 'Kurtarıldı! ✅' : (ok === 0 ? 'Kurtarılamadı' : 'Kısmen tamamlandı'),
            message: fail === 0
                ? `${ok} ürün başarıyla kurtarıldı ve stoğa işlendi!`
                : `${ok} ürün kurtarıldı. ${fail} üründe sorun oldu: ${failedNames.join(', ')}. Tekrar deneyebilirsin.`,
            type: fail === 0 ? 'success' : (ok === 0 ? 'error' : 'warning')
        });
    };

    // --- ACTION: HANDLE WASTE (NO) ---
    const handleWasteClick = (itemName, notifId, batchId = null) => {
        setWasteData({ itemName, notifId, batchId });
    };

    const openExpiredReviewModal = (notif) => {
        const items = getExpiredReviewItems(notif);
        setExpiredReviewModal({
            isOpen: true,
            notification: notif,
            selectedBatchIds: items.map((item) => Number(item.batchId)).filter(Number.isInteger)
        });
    };

    const toggleExpiredReviewSelection = (batchId) => {
        const normalizedBatchId = Number(batchId);
        setExpiredReviewModal((prev) => {
            const selected = new Set(prev.selectedBatchIds || []);
            if (selected.has(normalizedBatchId)) selected.delete(normalizedBatchId);
            else selected.add(normalizedBatchId);
            return { ...prev, selectedBatchIds: Array.from(selected) };
        });
    };

    const closeExpiredReviewModal = () => {
        setExpiredReviewModal({ isOpen: false, notification: null, selectedBatchIds: [] });
    };

    const resolveExpiredReview = async (action) => {
        const notification = expiredReviewModal.notification;
        const batchIds = expiredReviewModal.selectedBatchIds || [];
        if (!notification || batchIds.length === 0) return;

        try {
            const authToken = localStorage.getItem('authToken');
            const res = await fetch(`${API_BASE_URL}/api/inventory/expired-review/resolve`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
                },
                body: JSON.stringify({
                    notificationId: notification.id,
                    batchIds,
                    action
                })
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.error || 'SKT review aksiyonu uygulanamadı.');
            }

            const actionLabels = {
                WASTED: 'israf olarak kaydedildi',
                GIVEN: 'verildi olarak kaydedildi',
                CONSUMED: 'kullanıldı olarak kaydedildi',
                KEEP: '3 gün ertelendi',
                FALSE_ALARM: 'yanlış alarm olarak kapatıldı'
            };

            setFeedbackModal({
                isOpen: true,
                title: 'Review Güncellendi',
                message: `${batchIds.length} ürün ${actionLabels[action] || 'işlendi'}.`,
                type: 'success'
            });
            closeExpiredReviewModal();
            fetchNotifications();
            window.dispatchEvent(new CustomEvent('refreshNotifications'));
            window.dispatchEvent(new Event('Alakart_SYNC'));
            window.dispatchEvent(new Event('shoppingListUpdated'));
        } catch (err) {
            setFeedbackModal({
                isOpen: true,
                title: 'İşlem Başarısız',
                message: err.message || 'Bir hata oluştu, lütfen tekrar deneyin.',
                type: 'error'
            });
        }
    };

    // --- LOGIC: EXECUTE TRANSFORMATION ---
    const handleTransformationComplete = async () => {
        if (!transformationData) return;
        const { recipe, sourceItem, notifId, usedRecipeId } = transformationData;

        try {
            const authToken = localStorage.getItem('authToken');

            // --- ERP V3: ATOMIC HARVEST LOOP ---
            // If we have a usedRecipeId (from Home ERP Light v3), use the atomic endpoint
            if (usedRecipeId) {
                console.log(`🚀 [ERP-Production] Initiating atomic harvest for UsedRecipe: ${usedRecipeId}`);
                const harvestRes = await fetch(`${API_BASE_URL}/api/recipes/harvest`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken}`
                    },
                    body: JSON.stringify({
                        usedRecipeId: usedRecipeId,
                        harvestedPortion: 1,
                        dishId: recipe.id // The target recipe that produced the new item
                    })
                });

                if (!harvestRes.ok) {
                    const errorData = await harvestRes.json().catch(() => ({}));
                    throw new Error(errorData.error || 'Üretim işlemi başarısız oldu.');
                }

                const result = await harvestRes.json();
                console.log(`✅ [ERP-Production] Harvest successful:`, result);
            } else {
                // --- LEGACY FALLBACK (ONLY for non-ERP recipes) ---
                console.warn(`⚠️ [Legacy-Rescue] usedRecipeId missing, falling back to legacy transformation logic.`);
                if (recipe.rescueKind === 'PREPARATION') {
                    throw new Error('Hazırlık kurtarma akışı henüz state-write ile açılmadı; yeni kurtarma stoğu yazılmaz.');
                }
                const headers = authToken ? { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };

                // 1. Find and Delete OLD Item
                let oldItem = null;
                const { batchId } = transformationData;

                if (batchId) {
                    // Direct ID match from notification
                    oldItem = { id: batchId };
                } else {
                    oldItem = await findInventoryItemByName(sourceItem);
                }

                // 2. Create NEW Item first. If this fails, old stock must remain untouched.
                const newExpiryDate = new Date();
                newExpiryDate.setDate(newExpiryDate.getDate() + (recipe.extendedShelfLifeDays || 7));

                const createRes = await fetch(`${API_BASE_URL}/api/inventory`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                        ingredient_name: recipe.targetName,
                        // [Fix] Dönüşen ürünün dictionaryId'si — yoksa Diamond-Hard kontrat reddediyordu.
                        dictionaryId: recipe.targetDictionaryId || null,
                        // [Fix] Kurtarma değeri — purchase_price=0 olunca KPI (Kurtarılan/Değer) 0 görünüyordu.
                        purchase_price: recipe.estimatedValue || 0,
                        // [Fix] Miktar — eskiden sabit 1 + 'Kavanoz' gönderiliyordu; 'Kavanoz' gram
                        // karşılığı bilinmediği için stok "1 g" görünüyordu. Artık tarifin gerçek üretim
                        // miktarı (producedAmount, gram) + producedUnit='g' gönderilir → gerçekçi miktar.
                        remaining_amount: recipe.producedAmount || 1,
                        unit: recipe.producedUnit || recipe.unit || 'Adet',
                        expiry_date: newExpiryDate.toISOString().split('T')[0],
                        storage_location: recipe.storageLocation || 'Buzdolabı',
                        // [Fix #2] Kör 'Fridge' yerine tarifin storageLocation'ından türet (Kiler→Pantry,
                        // Buzdolabı→Fridge, Dondurucu→Freezer). Backend is_rescued'da bunu KORUR (respectMainZone).
                        // -> Taze Pesto Sos → Fridge/Soslar (auto), Kruton → Pantry/Tahıllar (regresyon yok).
                        main_zone: recipe.mainZone || ({ 'Kiler': 'Pantry', 'Buzdolabı': 'Fridge', 'Derin Dondurucu': 'Freezer', 'Dondurucu': 'Freezer' }[recipe.storageLocation] || 'Fridge'),
                        sub_zone: recipe.subZone || 'General',
                        is_rescued: true
                    })
                });
                if (!createRes.ok) {
                    const errorData = await createRes.json().catch(() => ({}));
                    throw new Error(errorData.error || 'Yeni kurtarma stoğu oluşturulamadı.');
                }

                if (oldItem) {
                    const deleteRes = await fetch(`${API_BASE_URL}/api/inventory/${oldItem.id}?isTransformation=true`, {
                        method: 'DELETE',
                        headers
                    });
                    if (!deleteRes.ok) {
                        const errorData = await deleteRes.json().catch(() => ({}));
                        throw new Error(errorData.error || 'Eski stok güvenli şekilde kapatılamadı.');
                    }
                }
            }

            // 3. Mark Notification as Read
            if (notifId) {
                await markAsRead(notifId, 'rescue_completed');
            }

            // 4. Close & UI feedback
            setTransformationData(null);

            // Trigger Auditor to clean up linked notifications if any
            await fetch(`${API_BASE_URL}/api/notifications/refresh-audit`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${authToken}` }
            }).catch(() => { });

            setFeedbackModal({
                isOpen: true,
                title: 'Başarılı!',
                message: `${sourceItem} başarıyla ${recipe.targetName}'na dönüştürüldü ve stoğa işlendi!`,
                type: 'success'
            });
            try {
                const outcome = {
                    sourceItemName: sourceItem,
                    transformedItemName: recipe.targetName,
                    daysSaved: Number(recipe.extendedShelfLifeDays || recipe.newExpiryDays || 0),
                    valueSavedTry: Number(recipe.estimatedValue || 0),
                    at: new Date().toISOString()
                };
                sessionStorage.setItem('onboarding_rescue_outcome', JSON.stringify(outcome));
            } catch (_storageError) {
                // no-op
            }
            window.dispatchEvent(new CustomEvent('refreshNotifications'));
            // 🔔 Sync Alakart/Shopping context as inventory changed
            window.dispatchEvent(new Event('Alakart_SYNC'));
            window.dispatchEvent(new Event('shoppingListUpdated'));

        } catch (err) {
            console.error('Transformation error:', err);
            setFeedbackModal({
                isOpen: true,
                title: 'İşlem Başarısız',
                message: err.message || 'Bir hata oluştu, lütfen tekrar deneyin.',
                type: 'error'
            });
        }
    };

    // --- LOGIC: EXECUTE WASTE DELETION ---
    const handleWasteConfirm = async () => {
        if (!wasteData) return;
        const { itemName } = wasteData;
        const authToken = localStorage.getItem('authToken');

        try {
            let itemToDelete = null;
            const { batchId } = wasteData;

            if (batchId) {
                // Direct ID match from notification metadata
                itemToDelete = { id: batchId };
            } else {
                // Fallback to name search
                itemToDelete = await findInventoryItemByName(itemName);
            }

            if (itemToDelete) {
                const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
                const res = await fetch(`${API_BASE_URL}/api/inventory/${itemToDelete.id}`, {
                    method: 'DELETE',
                    headers
                });

                if (!res.ok) {
                    throw new Error('Ürün silinemedi (Miktar yetersiz veya çoktan silinmiş olabilir)');
                }

                setFeedbackModal({
                    isOpen: true,
                    title: 'Silindi!',
                    message: `${itemName} stoktan düşüldü.`,
                    type: 'success'
                });
                window.dispatchEvent(new CustomEvent('refreshNotifications'));
                // 🔔 Sync Alakart/Shopping context as inventory changed
                window.dispatchEvent(new Event('Alakart_SYNC'));
                window.dispatchEvent(new Event('shoppingListUpdated'));
            } else {
                setFeedbackModal({
                    isOpen: true,
                    title: 'Bilgi',
                    message: 'Ürün stokta bulunamadı veya daha önce silinmiş.',
                    type: 'error'
                });
            }
            setWasteData(null);

            // Trigger Auditor to update notification message immediately
            await fetch(`${API_BASE_URL}/api/notifications/refresh-audit`, {
                method: 'POST',
                headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
            });

            fetchNotifications();
        } catch (err) {
            console.error('Waste error:', err);
        }
    };

    const getIcon = (type) => {
        switch (type) {
            case 'IKM_ALERT': return 'notifications_active';
            case 'IKM_AUDIT': return 'inventory';
            case 'INVENTORY_EXPIRED_REVIEW': return 'fact_check';
            case 'IKM_TRANSFORM': return 'auto_awesome';
            case 'ADMIN_BROADCAST': return 'campaign';
            case 'TARGETED': return 'campaign';
            case 'PAYMENT_VERIFIED': return 'workspace_premium';
            case 'PAYMENT_UNDER_REVIEW': return 'pending_actions';
            case 'PAYMENT_ACTION_REQUIRED': return 'payments';
            case 'SPECIAL_DAYS_ACCESS_AVAILABLE': return 'celebration';
            case 'SPECIAL_DAYS_ACCESS_ACTIVE': return 'event_available';
            case 'SPECIAL_DAYS_ACCESS_CONSUMED': return 'task_alt';
            case 'BREAKFAST_INTENT': return 'breakfast_dining';
            case 'IKM_AUTO_MENU_ONBOARDING': return 'restaurant_menu';
            case 'IKM_DAY7_MILESTONE': return 'shopping_cart_checkout';
            default: return 'info';
        }
    };

    const getIconColor = (type) => {
        switch (type) {
            case 'IKM_ALERT': return 'text-orange-500 bg-orange-50 dark:bg-orange-900/20';
            case 'IKM_AUDIT': return 'text-blue-500 bg-blue-50 dark:bg-blue-900/10';
            case 'INVENTORY_EXPIRED_REVIEW': return 'text-red-500 bg-red-50 dark:bg-red-900/10';
            case 'IKM_TRANSFORM': return 'text-green-500 bg-green-50 dark:bg-green-900/10';
            case 'ADMIN_BROADCAST': return 'text-primary bg-primary/5';
            case 'TARGETED': return 'text-primary bg-primary/5';
            case 'PAYMENT_VERIFIED': return 'text-emerald-500 bg-emerald-50 dark:bg-emerald-900/10';
            case 'PAYMENT_UNDER_REVIEW': return 'text-amber-500 bg-amber-50 dark:bg-amber-900/10';
            case 'PAYMENT_ACTION_REQUIRED': return 'text-rose-500 bg-rose-50 dark:bg-rose-900/10';
            case 'SPECIAL_DAYS_ACCESS_AVAILABLE': return 'text-fuchsia-500 bg-fuchsia-50 dark:bg-fuchsia-900/10';
            case 'SPECIAL_DAYS_ACCESS_ACTIVE': return 'text-sky-500 bg-sky-50 dark:bg-sky-900/10';
            case 'SPECIAL_DAYS_ACCESS_CONSUMED': return 'text-indigo-500 bg-indigo-50 dark:bg-indigo-900/10';
            case 'BREAKFAST_INTENT': return 'text-amber-500 bg-amber-50 dark:bg-amber-900/10';
            case 'IKM_AUTO_MENU_ONBOARDING': return 'text-primary bg-primary/10';
            case 'IKM_DAY7_MILESTONE': return 'text-emerald-500 bg-emerald-50 dark:bg-emerald-900/10';
            default: return 'text-slate-500 bg-slate-50';
        }
    };

    const isSystemNotification = (notif) => {
        const type = String(notif?.type || '');
        return [
            'ADMIN_BROADCAST',
            'TARGETED',
            'PAYMENT_VERIFIED',
            'PAYMENT_UNDER_REVIEW',
            'PAYMENT_ACTION_REQUIRED',
            'SPECIAL_DAYS_ACCESS_AVAILABLE',
            'SPECIAL_DAYS_ACCESS_ACTIVE',
            'SPECIAL_DAYS_ACCESS_CONSUMED'
        ].includes(type);
    };

    const isLegacyUiTriggerNotification = (notif) => LEGACY_UI_TRIGGER_TYPES.has(String(notif?.type || '').toUpperCase());

    const handleAutoMenuOnboardingResponse = async (notifId, action) => {
        console.info('[Notifications] Legacy auto menu onboarding UI is frozen; response ignored.', { notifId, action });
        return;

        try {
            const authToken = localStorage.getItem('authToken');
            await fetch(`${API_BASE_URL}/api/notifications/respond-auto-menu-onboarding`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({ notificationId: notifId, action })
            });
            fetchNotifications();
            window.dispatchEvent(new CustomEvent('refreshNotifications'));
        } catch (err) {
            console.error('Auto menu onboarding response error:', err);
        }
    };

    const handleOpenBreakfastModal = (_notif) => {
        setBreakfastModalOpen(true);
    };

    const handleSchoolLunchSubmit = async ({ status, child }) => {
        const { notifId, pendingAction } = schoolLunchModal;
        try {
            const authToken = localStorage.getItem('authToken');
            await fetch(`${API_BASE_URL}/api/notifications/respond-lunchbox-init`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({ status, child, notificationId: notifId })
            });

            // Even if it fails, we want to proceed with the rescue
            if (pendingAction) {
                // Call handleRescueClick AGAIN but with showOnboarding=false to avoid loop
                handleRescueClick(pendingAction.itemName, pendingAction.notifId, pendingAction.transformId, false);
            }
        } catch (err) {
            console.error('School lunch response error:', err);
            // Proceed anyway
            if (pendingAction) {
                handleRescueClick(pendingAction.itemName, pendingAction.notifId, pendingAction.transformId, false);
            }
        } finally {
            setSchoolLunchModal({ isOpen: false, notifId: null, pendingAction: null });
            fetchNotifications(); // Refresh to update onboarding status in store/state if needed
        }
    };

    const filteredNotifications = () => {
        const visibleNotifications = notifications.filter((notif) => {
            const type = String(notif?.type || '').toUpperCase();
            if (HIDDEN_BREAKFAST_NOTIFICATION_TYPES.has(type)) return false;
            if (type === 'BREAKFAST_INTENT' && notif.isRead) return false;
            return true;
        });
        if (activeTab === 'ALL') return visibleNotifications;
        if (activeTab === 'SYSTEM' || activeTab === 'BROADCAST') return visibleNotifications.filter(isSystemNotification);
        if (activeTab === 'IKM_TRANSFORM') return visibleNotifications.filter(n => n.type === 'IKM_TRANSFORM');
        if (activeTab === 'INVENTORY_EXPIRED_REVIEW') return visibleNotifications.filter(n => n.type === 'INVENTORY_EXPIRED_REVIEW');
        return visibleNotifications.filter(n => n.type === activeTab);
    };

    const rawLatestBreakfastNotification = notifications
        .filter((notif) => (
            !notif.isRead
            && !isLegacyUiTriggerNotification(notif)
            && (notif.type === 'BREAKFAST_INTENT' || notif.targetFilters?.action === 'open_breakfast_blue_modal')
        ))
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0] || null;
    const latestBreakfastNotification = rawLatestBreakfastNotification?.id === completedBreakfastNotificationId
        ? null
        : rawLatestBreakfastNotification;
    const latestUnreadMealIntentNotification = notifications
        .filter((notif) => !notif.isRead && notif.type === 'SMART_SEARCH_MEAL_INTENT')
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0] || null;
    const hasSupplementalNotificationActions = Boolean(
        mobilePushReminder?.variant === 'needs_mobile_opt_in' || latestBreakfastNotification
    );

    const getTabUnreadCount = (tabId) => {
        const unread = notifications.filter((n) => !n.isRead);
        if (tabId === 'ALL') return unread.length;
        if (tabId === 'SYSTEM' || tabId === 'BROADCAST') return unread.filter(isSystemNotification).length;
        if (tabId === 'IKM_TRANSFORM') return unread.filter((n) => n.type === 'IKM_TRANSFORM').length;
        if (tabId === 'INVENTORY_EXPIRED_REVIEW') return unread.filter((n) => n.type === 'INVENTORY_EXPIRED_REVIEW').length;
        return unread.filter((n) => n.type === tabId).length;
    };

    const notificationTabs = [
        { id: 'BROADCAST', label: 'Sistem Mesajları', icon: 'campaign' },
        { id: 'IKM_ALERT', label: 'Tazelik Özeti', icon: 'notifications_active' },
        { id: 'IKM_TRANSFORM', label: 'Akıllı Mutfak Önerisi', icon: 'auto_awesome' },
        { id: 'INVENTORY_EXPIRED_REVIEW', label: 'SKT Review', icon: 'fact_check' },
        { id: 'IKM_AUDIT', label: 'Mutfak Denetimi', icon: 'inventory' },
        { id: 'ALL', label: 'Tümü', icon: 'apps' }
    ];

    const handleShowAllDefaultChange = (checked) => {
        setShowAllByDefault(checked);
        try {
            localStorage.setItem('notifications_show_all_default', checked ? 'true' : 'false');
        } catch (e) {
            // ignore storage issues
        }
        setActiveTab(checked ? 'ALL' : 'BROADCAST');
    };

    useEffect(() => {
        if (!isMobilePushRuntime || !latestBreakfastNotification || breakfastModalOpen || latestUnreadMealIntentNotification) return;
        try {
            const key = `breakfast_auto_open_seen:${latestBreakfastNotification.id}`;
            if (sessionStorage.getItem(key) === '1') return;
            sessionStorage.setItem(key, '1');
            handleOpenBreakfastModal(latestBreakfastNotification);
        } catch (_error) {
            handleOpenBreakfastModal(latestBreakfastNotification);
        }
    }, [isMobilePushRuntime, latestBreakfastNotification, latestUnreadMealIntentNotification, breakfastModalOpen]);

    // --- PARSER: Extract items from notification text ---
    const parseItemsFromMessage = (message) => {
        // Find everything after the first colon and before the first period after it
        const match = message.match(/:\s(.*?)\.\s/);
        if (match && match[1]) {
            return match[1].split(',').map(s => s.trim());
        }
        return [];
    };

    return (
        <div className="max-w-7xl mx-auto w-full px-4 py-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                <div className="relative">
                    <h1 className="text-xl font-bold text-slate-900 dark:text-white uppercase tracking-tight">
                        Mutfak <span className="text-primary">Bildirimleri</span>
                    </h1>
                    <div className="absolute -bottom-1 left-0 w-12 h-0.5 bg-primary rounded-full"></div>
                    <p className="mt-1 text-slate-500 dark:text-gray-400 font-bold uppercase text-[8px] tracking-wider">Akıllı Mutfak Yönetim Sistemi</p>
                </div>

                <div className="flex flex-row gap-2 sm:gap-3">
                    {pushUiState.showPrimary ? (
                        <button
                            onClick={pushUiState.disabled ? undefined : handleEnableWebPush}
                            disabled={pushUiState.disabled}
                            className={`flex h-10 px-4 items-center justify-center gap-2 rounded-xl border transition-all font-black text-[9px] uppercase tracking-wider disabled:opacity-80 ${pushUiState.className} ${pushUiState.disabled ? 'cursor-default' : 'hover:scale-105 active:scale-95'}`}
                        >
                            <span className="material-symbols-outlined text-[16px]">notifications_active</span>
                            {pushUiState.label}
                        </button>
                    ) : null}
                    {pushUiState.showRepair ? (
                        <button
                            onClick={handleEnableWebPush}
                            disabled={pushBusy}
                            className="flex h-10 px-4 items-center justify-center gap-2 rounded-xl bg-emerald-50 dark:bg-emerald-900/10 text-emerald-700 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-900/20 transition-all hover:bg-emerald-100 dark:hover:bg-emerald-900/20 active:scale-95 font-black text-[9px] uppercase tracking-wider disabled:opacity-60"
                        >
                            <span className="material-symbols-outlined text-[16px]">sync</span>
                            YENİDEN KAYDET
                        </button>
                    ) : null}
                    {notifications.some(n => !n.isRead) && (
                        <button
                            onClick={markAllRead}
                            className="flex h-10 px-4 items-center justify-center gap-2 rounded-xl bg-orange-50 dark:bg-orange-900/10 text-orange-600 dark:text-orange-400 border border-orange-100 dark:border-orange-900/20 transition-all hover:bg-orange-100 dark:hover:bg-orange-900/20 active:scale-95 font-black text-[9px] uppercase tracking-wider"
                        >
                            <span className="material-symbols-outlined text-[16px]">done_all</span>
                            HEPSİ TAMAM
                        </button>
                    )}
                    <button
                        onClick={() => navigate(-1)}
                        className="flex-1 flex h-10 px-4 items-center justify-center gap-2 rounded-xl bg-white dark:bg-gray-800 shadow-lg shadow-black/5 ring-1 ring-black/5 dark:ring-white/5 transition-all hover:scale-105 active:scale-95 text-slate-600 dark:text-gray-300 font-bold text-xs"
                    >
                        <span className="material-symbols-outlined text-base">arrow_back</span>
                        GERİ DÖN
                    </button>
                    <button
                        onClick={fetchNotifications}
                        className="flex-1 flex h-10 px-4 items-center justify-center gap-2 rounded-xl bg-primary text-white shadow-lg shadow-primary/20 transition-all hover:scale-105 active:scale-95 font-bold text-xs uppercase"
                    >
                        <span className="material-symbols-outlined text-[18px]">refresh</span>
                        Yenile
                    </button>
                </div>
            </div>

            <div className="flex flex-col gap-1 rounded-2xl border border-slate-100 bg-white p-1.5 shadow-xl dark:border-white/5 dark:bg-gray-900 sm:flex-row sm:items-center sm:overflow-x-auto sm:whitespace-nowrap no-scrollbar scroll-smooth mb-8">
                {notificationTabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex w-full items-center gap-2 overflow-hidden rounded-xl px-4 py-3 text-[10px] font-bold transition-all sm:w-auto sm:rounded-lg sm:py-1.5 sm:text-[9px] ${
                            activeTab === tab.id
                                ? 'bg-primary text-white shadow-md shadow-primary/20 sm:scale-105'
                                : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600 dark:hover:bg-white/5 dark:hover:text-gray-300'
                        }`}
                    >
                        <span className="material-symbols-outlined shrink-0 text-[16px] sm:text-[14px]">{tab.icon}</span>
                        <span className="truncate">{tab.label}</span>
                        {getTabUnreadCount(tab.id) > 0 && (
                            <span className={`ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1 text-[10px] font-black ${
                                activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-primary text-white'
                            }`}>
                                {getTabUnreadCount(tab.id) > 9 ? '9+' : getTabUnreadCount(tab.id)}
                            </span>
                        )}
                    </button>
                ))}

                <label className="flex items-center gap-2 rounded-xl px-4 py-3 text-[10px] font-bold text-slate-500 dark:text-slate-300 sm:ml-auto sm:rounded-lg sm:py-1.5 sm:text-[9px]">
                    <input
                        type="checkbox"
                        checked={showAllByDefault}
                        onChange={(e) => handleShowAllDefaultChange(e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                    />
                    <span className="truncate">Tüm mesajları açık tut</span>
                </label>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4 bg-white dark:bg-gray-900 rounded-[2rem] shadow-xl border border-slate-100 dark:border-white/5">
                    <div className="relative">
                        <div className="w-12 h-12 border-4 border-primary/20 rounded-full"></div>
                        <div className="absolute top-0 left-0 w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                    </div>
                    <p className="text-slate-400 font-bold uppercase text-[9px] tracking-widest animate-pulse">Tarama Yapılıyor...</p>
                </div>
            ) : filteredNotifications().length === 0 && !hasSupplementalNotificationActions ? (
                <div className="bg-white dark:bg-gray-900 rounded-[2rem] p-12 text-center shadow-xl border border-slate-100 dark:border-white/5 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-48 h-48 bg-primary/5 rounded-full blur-3xl -mr-24 -mt-24"></div>
                    <div className="size-16 bg-slate-50 dark:bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner ring-1 ring-black/5">
                        <span className="material-symbols-outlined text-3xl text-slate-300">notifications_off</span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2 tracking-tight">TEMİZ BİR LİSTE!</h3>
                    <p className="text-slate-500 dark:text-gray-400 text-sm max-w-sm mx-auto font-medium leading-relaxed">
                        Bu kategoride şu an için bekleyen bir bildirim yok.
                    </p>
                </div>
            ) : (
                <div className="grid gap-8">
                    {mobilePushReminder?.variant === 'needs_mobile_opt_in' && (
                        <div className="rounded-[2rem] border border-sky-200 bg-sky-50 px-5 py-4 shadow-sm dark:border-sky-900/30 dark:bg-sky-900/10">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-[10px] font-black uppercase tracking-[0.22em] text-sky-600 dark:text-sky-300">Mobil Web Push</p>
                                    <p className="mt-2 text-sm font-semibold text-slate-800 dark:text-white">
                                        Bu hesap masaustunde push icin kayitli. Telefonda da bildirim almak icin bu cihazda web push izni vermen gerekiyor.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleEnableWebPush}
                                    disabled={pushBusy}
                                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-sky-500 px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-white shadow-lg shadow-sky-500/20 transition hover:bg-sky-600 disabled:opacity-60"
                                >
                                    <span className="material-symbols-outlined text-[18px]">smartphone</span>
                                    Telefonda Bildirimi Ac
                                </button>
                            </div>
                        </div>
                    )}

                    {latestBreakfastNotification && (
                        <div className="rounded-[2rem] border border-amber-200 bg-amber-50 px-5 py-4 shadow-sm dark:border-amber-900/30 dark:bg-amber-900/10">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-600 dark:text-amber-300">Kahvalti Akisi</p>
                                    <p className="mt-2 text-sm font-semibold text-slate-800 dark:text-white">
                                        Kahvalti secimini tamamlamadiysan buradan tekrar acip devam edebilirsin.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        handleOpenBreakfastModal(latestBreakfastNotification);
                                    }}
                                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-amber-500 px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-white shadow-lg shadow-amber-500/20 transition hover:bg-amber-600"
                                >
                                    <span className="material-symbols-outlined text-[18px]">breakfast_dining</span>
                                    Kahvaltini Sec
                                </button>
                            </div>
                        </div>
                    )}

                    {filteredNotifications().map((notif) => {
                        const safeTitle = repairMojibake(notif.title || '');
                        const safeMessage = repairMojibake(notif.message || '');
                        // Check for actionable items
                        // IKM_ALERT -> Rescue Only
                        // IKM_AUDIT -> Waste Only
                        const items = parseItemsFromMessage(safeMessage);

                        // Waste Logic (Audit only)
                        const wasteItems = (notif.type === 'IKM_AUDIT') ? items : [];

                        return (
                            <div
                                key={notif.id}
                                onClick={() => handleNotificationClick(notif)}
                                className={`group relative bg-white dark:bg-gray-900 p-6 rounded-[2rem] shadow-lg transition-all border cursor-pointer hover:scale-[1.01] active:scale-[0.99]
                                ${notif.isRead
                                        ? 'border-transparent opacity-70 grayscale-[0.5]'
                                        : 'border-primary/10 dark:border-primary/20 ring-4 ring-primary/5 hover:ring-primary/10'}`}
                            >
                                <div className="flex flex-col md:flex-row items-start gap-6">
                                    <div className={`size-14 rounded-2xl flex items-center justify-center shrink-0 shadow-xl ${getIconColor(notif.type)} shadow-current/5`}>
                                        <span className="material-symbols-outlined text-2xl">{getIcon(notif.type)}</span>
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex flex-wrap items-center gap-2 mb-2">
                                            <span className={`text-[9px] font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-md ${getIconColor(notif.type)} shadow-current/5`}>
                                                {notif.type === 'IKM_ALERT' ? 'TAZELİK ÖZETİ' :
                                                    notif.type === 'IKM_AUDIT' ? 'MUTFAK DENETİMİ' :
                                                        notif.type === 'INVENTORY_EXPIRED_REVIEW' ? 'SKT REVIEW' :
                                                            notif.type === 'IKM_TRANSFORM' ? 'AKILLI MUTFAK ÖNERİSİ' :
                                                            notif.type === 'ADMIN_BROADCAST' ? 'SİSTEM DUYURUSU' :
                                                                notif.type === 'SPECIAL_DAYS_ACCESS_AVAILABLE' ? 'ÖZEL GÜNLER HAKKI' :
                                                                    notif.type === 'SPECIAL_DAYS_ACCESS_ACTIVE' ? 'ÖZEL GÜNLER AKTİF' :
                                                                        notif.type === 'SPECIAL_DAYS_ACCESS_CONSUMED' ? 'ÖZEL GÜNLER TÜKETİLDİ' :
                                                                            notif.type === 'IKM_DAY7_MILESTONE' ? '7. GÜN TAMAMLANDI' :
                                                                notif.type.replace('_', ' ')}
                                            </span>
                                            <span className="text-[10px] font-bold text-slate-400 dark:text-gray-500 uppercase">
                                                {new Date(notif.createdAt).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>

                                        <h4 className={`text-lg font-bold mb-2 tracking-tight uppercase ${notif.isRead ? 'text-slate-700 dark:text-gray-400' : 'text-slate-900 dark:text-white'}`}>
                                            {safeTitle}
                                        </h4>

                                        <p className={`text-sm leading-relaxed font-medium mb-6 ${notif.isRead ? 'text-slate-500 dark:text-gray-500' : 'text-slate-600 dark:text-gray-300'}`}>
                                            {safeMessage}
                                        </p>

                                        {/* ACTION BUTTONS FOR RESCUE (IKM_TRANSFORM ONLY as requested) */}
                                        {notif.type === 'IKM_TRANSFORM' && !notif.isRead && (() => {
                                            const transformations = notif.targetFilters?.transformations ||
                                                parseItemsFromMessage(safeMessage)
                                                    .filter(itemName => getRescueRecipe(itemName.includes(' → ') ? itemName.split(' → ')[0].trim() : itemName))
                                                    .map(item => {
                                                        const cleanName = item.includes(' → ') ? item.split(' → ')[0].trim() : item;
                                                        return { ingredientName: cleanName, transform_id: null };
                                                    });

                                            if (transformations.length === 0) return null;

                                            // If only 1 option, show direct button
                                            if (transformations.length === 1) {
                                                const t = transformations[0];
                                                return (
                                                    <div className="mb-4">
                                                        <div className="w-full sm:w-auto flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/10 rounded-xl border border-green-100 dark:border-green-900/20">
                                                            <span className="text-xs font-bold px-2 text-green-800 dark:text-green-300">{t.ingredientName} Kurtar?</span>
                                                            <button
                                                                onClick={() => handleRescueClick(t.ingredientName, notif.id, t.transform_id, !!notif.targetFilters?.showLunchboxOnboarding && t.isLunchbox, t.batchId)}
                                                                className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-[10px] font-black uppercase flex items-center gap-1 transition-colors shadow-lg shadow-green-500/20"
                                                            >
                                                                <span className="material-symbols-outlined text-[14px]">recycling</span>
                                                                KURTAR (EVET)
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            }

                                            // If 2+ options, show modal trigger button
                                            return (
                                                <div className="mb-4">
                                                    <button
                                                        onClick={() => { setRescueSelected(new Set()); setRescueSelectionModal({ isOpen: true, transformations, notifId: notif.id, showLunchboxOnboarding: !!notif.targetFilters?.showLunchboxOnboarding }); }}
                                                        className="w-full sm:w-auto px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl text-xs font-black uppercase flex items-center justify-center gap-2 transition-all shadow-lg shadow-green-500/30 hover:shadow-green-500/50 hover:scale-[1.02] active:scale-[0.98]"
                                                    >
                                                        <span className="material-symbols-outlined text-lg">recycling</span>
                                                        <span>{transformations.length} KURTARMA SEÇENEĞİ</span>
                                                    </button>
                                                </div>
                                            );
                                        })()}

                                        {/* ACTION BUTTONS FOR WASTE (IKM_AUDIT ONLY) */}
                                        {wasteItems.length > 0 && !notif.isRead && (
                                            <div className="mb-4 flex flex-wrap gap-2">
                                                {wasteItems.map(item => (
                                                    <div key={item} className="w-full sm:w-auto flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/10 rounded-xl border border-red-100 dark:border-red-900/20">
                                                        <span className="text-xs font-bold px-2 text-red-800 dark:text-red-300">{item} Sil?</span>
                                                        <button
                                                            onClick={() => {
                                                                // For Audit notifications, we might have mapped batchIds in targetFilters
                                                                const batchId = notif.targetFilters?.batchIds ? notif.targetFilters.batchIds[items.indexOf(item)] : null;
                                                                handleWasteClick(item, notif.id, batchId);
                                                            }}
                                                            className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-[10px] font-black uppercase flex items-center gap-1 transition-colors shadow-lg shadow-red-500/20"
                                                        >
                                                            <span className="material-symbols-outlined text-[14px]">delete</span>
                                                            ONAYLA (ATIK)
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {notif.type === 'INVENTORY_EXPIRED_REVIEW' && !notif.isRead && (() => {
                                            const reviewItems = getExpiredReviewItems(notif);
                                            if (reviewItems.length === 0) return null;
                                            return (
                                                <div className="mb-4 rounded-2xl border border-red-100 dark:border-red-900/30 bg-red-50/70 dark:bg-red-900/10 p-4">
                                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                                        <div>
                                                            <p className="text-xs font-black uppercase tracking-widest text-red-700 dark:text-red-300">SKT geçmiş ürün denetimi</p>
                                                            <p className="mt-1 text-sm font-semibold text-slate-700 dark:text-slate-300">
                                                                {reviewItems.slice(0, 3).map(formatExpiredReviewItemLabel).join(', ')}
                                                                {reviewItems.length > 3 ? ` +${reviewItems.length - 3} ürün` : ''}
                                                            </p>
                                                        </div>
                                                        <button
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                openExpiredReviewModal(notif);
                                                            }}
                                                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-500 px-4 py-3 text-xs font-black uppercase tracking-widest text-white shadow-lg shadow-red-500/20 transition-all hover:bg-red-600 active:scale-[0.98]"
                                                        >
                                                            <span className="material-symbols-outlined text-lg">fact_check</span>
                                                            Toplu Review Aç
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {!notif.isRead
                                            && !isLegacyUiTriggerNotification(notif)
                                            && notif.id !== completedBreakfastNotificationId
                                            && (notif.type === 'BREAKFAST_INTENT' || notif.targetFilters?.action === 'open_breakfast_blue_modal') && (
                                            <div className="mb-4">
                                                <button
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleOpenBreakfastModal(notif);
                                                    }}
                                                    className="mt-3 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-xs font-black shadow-lg shadow-orange-500/20 hover:shadow-orange-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all uppercase tracking-widest flex items-center justify-center gap-2"
                                                >
                                                    <span className="material-symbols-outlined">breakfast_dining</span>
                                                    BUGÜNKÜ KAHVALTI ÖNERİLERİNİ AÇ
                                                </button>
                                            </div>
                                        )}

                                        {/* AUTO MENU ONBOARDING ACTIONS */}
                                        {!notif.isRead && !isLegacyUiTriggerNotification(notif) && notif.type === 'IKM_AUTO_MENU_ONBOARDING' && (
                                            <div className="mb-4 flex flex-col gap-3">
                                                <div className="flex flex-wrap gap-2">
                                                    <button
                                                        onClick={() => handleAutoMenuOnboardingResponse(notif.id, 'no')}
                                                        className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-500 rounded-lg text-[10px] font-bold hover:bg-slate-200 transition-all uppercase tracking-wider"
                                                    >
                                                        HAYIR, KALSIN
                                                    </button>
                                                    <button
                                                        onClick={() => handleAutoMenuOnboardingResponse(notif.id, 'remind')}
                                                        className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-500 rounded-lg text-[10px] font-bold hover:bg-slate-200 transition-all uppercase tracking-wider"
                                                    >
                                                        DAHA SONRA
                                                    </button>
                                                    <button
                                                        onClick={() => handleAutoMenuOnboardingResponse(notif.id, 'yes')}
                                                        className="px-6 py-2 bg-primary text-white rounded-lg text-[10px] font-bold shadow-lg shadow-primary/30 hover:scale-105 transition-all uppercase tracking-wider flex items-center gap-2"
                                                    >
                                                        <span className="material-symbols-outlined text-sm">check_circle</span>
                                                        EVET, AYARLAYALIM
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {!isLegacyUiTriggerNotification(notif) && notif.type === 'IKM_DAY7_MILESTONE' && (
                                            <div className="mb-4">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate('/my-shopping-lists', {
                                                            state: {
                                                                openSmartList: true,
                                                                source: 'day7_milestone'
                                                            }
                                                        });
                                                    }}
                                                    className="mt-1 px-6 py-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl text-xs font-black shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all uppercase tracking-widest flex items-center justify-center gap-2"
                                                >
                                                    <span className="material-symbols-outlined">shopping_cart_checkout</span>
                                                    ALIŞVERİŞ SEPETİ OLUŞTUR
                                                </button>
                                            </div>
                                        )}



                                        {!notif.isRead && notif.type !== 'IKM_TRANSFORM' && (
                                            <button
                                                onClick={() => markAsRead(notif.id)}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 dark:bg-slate-800/50 text-slate-400 dark:text-slate-500 hover:text-primary dark:hover:text-primary hover:bg-white dark:hover:bg-slate-800 rounded-lg border border-transparent hover:border-slate-200 dark:hover:border-slate-700 transition-all text-[9px] font-black uppercase tracking-widest group"
                                            >
                                                <span className="material-symbols-outlined text-sm group-hover:scale-110 transition-transform">done</span>
                                                OKUDUM
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* MODALS */}
            {transformationData && (
                <TransformationCard
                    isOpen={!!transformationData}
                    onClose={() => setTransformationData(null)}
                    data={transformationData.recipe}
                    sourceItemName={transformationData.sourceItem}
                    babyName={babyName}
                    hasBaby={profile?.hasBaby}
                    children={profile?.children || []}
                    onComplete={handleTransformationComplete}
                />
            )}

            {wasteData && (
                <WasteConfirmationModal
                    isOpen={!!wasteData}
                    onClose={() => setWasteData(null)}
                    onConfirm={handleWasteConfirm}
                    itemName={wasteData.itemName}
                />
            )}

            {expiredReviewModal.isOpen && (() => {
                const reviewItems = getExpiredReviewItems(expiredReviewModal.notification);
                const selected = new Set(expiredReviewModal.selectedBatchIds || []);
                const selectedCount = selected.size;
                const actionDisabled = selectedCount === 0;
                const actionButtonClass = "inline-flex flex-1 min-w-[140px] items-center justify-center gap-2 rounded-xl px-4 py-3 text-xs font-black uppercase tracking-widest text-white transition-all disabled:cursor-not-allowed disabled:opacity-40";

                return (
                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
                        onClick={closeExpiredReviewModal}
                    >
                        <div
                            className="w-full max-w-3xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-gray-900"
                            onClick={(event) => event.stopPropagation()}
                        >
                            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-5 dark:border-slate-800">
                                <div>
                                    <p className="text-[10px] font-black uppercase tracking-[0.22em] text-red-500">SKT Geçmiş Ürün Review</p>
                                    <h3 className="mt-1 text-xl font-black text-slate-900 dark:text-white">Toplu Denetim</h3>
                                    <p className="mt-1 text-sm font-semibold text-slate-500 dark:text-slate-400">
                                        {selectedCount} / {reviewItems.length} ürün seçili
                                    </p>
                                </div>
                                <button
                                    onClick={closeExpiredReviewModal}
                                    className="flex size-10 items-center justify-center rounded-xl bg-slate-100 text-slate-500 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"
                                >
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>

                            <div className="max-h-[52vh] overflow-y-auto px-6 py-4">
                                <div className="mb-3 flex items-center justify-between">
                                    <button
                                        onClick={() => setExpiredReviewModal((prev) => ({
                                            ...prev,
                                            selectedBatchIds: reviewItems.map((item) => Number(item.batchId)).filter(Number.isInteger)
                                        }))}
                                        className="text-xs font-black uppercase tracking-widest text-primary"
                                    >
                                        Tümünü Seç
                                    </button>
                                    <button
                                        onClick={() => setExpiredReviewModal((prev) => ({ ...prev, selectedBatchIds: [] }))}
                                        className="text-xs font-black uppercase tracking-widest text-slate-400"
                                    >
                                        Seçimi Temizle
                                    </button>
                                </div>

                                <div className="space-y-2">
                                    {reviewItems.map((item) => {
                                        const checked = selected.has(Number(item.batchId));
                                        const daysExpired = Number(item.daysExpired || 0);
                                        const dayText = daysExpired <= 0 ? 'bugün geçmiş' : `${daysExpired} gün geçmiş`;
                                        return (
                                            <label
                                                key={item.batchId}
                                                className={`flex cursor-pointer items-center gap-4 rounded-2xl border p-4 transition-all ${checked ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20' : 'border-slate-100 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/50'}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => toggleExpiredReviewSelection(item.batchId)}
                                                    className="size-5 accent-red-500"
                                                />
                                                <div className="min-w-0 flex-1">
                                                    <p className="truncate text-sm font-black text-slate-900 dark:text-white">
                                                        {formatExpiredReviewItemLabel(item)}
                                                    </p>
                                                    <p className="mt-1 text-xs font-bold text-red-600 dark:text-red-300">
                                                        {dayText}
                                                    </p>
                                                </div>
                                                {Number(item.estimatedValueTry || 0) > 0 && (
                                                    <div className="text-right text-xs font-black text-slate-500 dark:text-slate-400">
                                                        {Number(item.estimatedValueTry || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} TL
                                                    </div>
                                                )}
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="border-t border-slate-100 bg-slate-50 px-6 py-5 dark:border-slate-800 dark:bg-slate-950/50">
                                <div className="flex flex-wrap gap-3">
                                    <button disabled={actionDisabled} onClick={() => resolveExpiredReview('WASTED')} className={`${actionButtonClass} bg-red-500 hover:bg-red-600`}>
                                        <span className="material-symbols-outlined text-lg">delete</span>
                                        İsraf Ettim
                                    </button>
                                    <button disabled={actionDisabled} onClick={() => resolveExpiredReview('GIVEN')} className={`${actionButtonClass} bg-blue-500 hover:bg-blue-600`}>
                                        <span className="material-symbols-outlined text-lg">volunteer_activism</span>
                                        Verdim
                                    </button>
                                    <button disabled={actionDisabled} onClick={() => resolveExpiredReview('CONSUMED')} className={`${actionButtonClass} bg-emerald-500 hover:bg-emerald-600`}>
                                        <span className="material-symbols-outlined text-lg">restaurant</span>
                                        Kullandım
                                    </button>
                                    <button disabled={actionDisabled} onClick={() => resolveExpiredReview('KEEP')} className={`${actionButtonClass} bg-slate-600 hover:bg-slate-700`}>
                                        <span className="material-symbols-outlined text-lg">schedule</span>
                                        3 Gün Ertele
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            <SuccessModal
                isOpen={feedbackModal.isOpen}
                onClose={() => setFeedbackModal(prev => ({ ...prev, isOpen: false }))}
                title={feedbackModal.title}
                message={feedbackModal.message}
                type={feedbackModal.type}
            />

            {/* RESCUE SELECTION MODAL */}
            {rescueSelectionModal.isOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
                    onClick={() => { if (!rescueProcessing) { setRescueSelectionModal({ isOpen: false, transformations: [], notifId: null, showLunchboxOnboarding: false }); setRescueSelected(new Set()); } }}
                >
                    <div
                        className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden border border-slate-200 dark:border-slate-700 animate-in zoom-in-95 duration-200 flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="sticky top-0 bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="size-10 bg-white/20 rounded-xl flex items-center justify-center">
                                    <span className="material-symbols-outlined text-white text-2xl">recycling</span>
                                </div>
                                <div>
                                    <h3 className="text-lg font-black text-white uppercase tracking-tight">Kurtarma Seçenekleri</h3>
                                    <p className="text-xs text-white/80 font-medium">{rescueSelectionModal.transformations.length} tarif · tikle seç, tek seferde kurtar</p>
                                </div>
                            </div>
                            <button
                                onClick={() => { if (!rescueProcessing) { setRescueSelectionModal({ isOpen: false, transformations: [], notifId: null, showLunchboxOnboarding: false }); setRescueSelected(new Set()); } }}
                                className="size-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors disabled:opacity-40"
                                disabled={rescueProcessing}
                            >
                                <span className="material-symbols-outlined text-white text-xl">close</span>
                            </button>
                        </div>

                        {/* Tümünü seç barı */}
                        {rescueSelectionModal.transformations.length > 1 && (
                            <div className="px-4 pt-3">
                                <button
                                    onClick={toggleRescueSelectAll}
                                    disabled={rescueProcessing}
                                    className="text-xs font-bold text-green-700 dark:text-green-400 hover:underline disabled:opacity-40"
                                >
                                    {rescueSelectionModal.transformations.every((t, i) => rescueSelected.has(rescueKeyOf(t, i))) ? '✕ Seçimi Temizle' : '✓ Tümünü Seç'}
                                </button>
                            </div>
                        )}

                        {/* Recipe List (tikle seç) */}
                        <div className="p-4 overflow-y-auto flex-1 space-y-3">
                            {rescueSelectionModal.transformations.map((t, index) => {
                                const recipe = getRescueRecipe(t.transform_id || t.ingredientName);
                                const targetName = recipe?.targetName || t.target || 'Kurtarma Tarifi';
                                const key = rescueKeyOf(t, index);
                                const selected = rescueSelected.has(key);

                                return (
                                    <button
                                        key={key}
                                        onClick={() => { if (!rescueProcessing) toggleRescueSelect(key); }}
                                        disabled={rescueProcessing}
                                        aria-pressed={selected}
                                        className={`w-full p-4 rounded-2xl border-2 transition-all active:scale-[0.98] shadow-sm text-left ${selected
                                            ? 'bg-green-100 dark:bg-green-900/40 border-green-500 dark:border-green-500 shadow-md'
                                            : 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800 hover:border-green-400'} ${rescueProcessing ? 'opacity-60' : ''}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="size-12 bg-white dark:bg-slate-800 rounded-xl flex items-center justify-center shrink-0 shadow-md">
                                                <span className="material-symbols-outlined text-green-600 dark:text-green-400 text-2xl">restaurant</span>
                                            </div>
                                            <div className="flex-1 text-left">
                                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                    <span className="text-sm font-bold text-gray-900 dark:text-white">{t.ingredientName}</span>
                                                    <span className="material-symbols-outlined text-green-600 dark:text-green-400 text-sm">arrow_forward</span>
                                                    <span className="text-sm font-bold text-green-700 dark:text-green-400">{targetName}</span>
                                                </div>
                                                {t.extra_days && (
                                                    <div className="text-xs text-green-600 dark:text-green-500 font-medium">
                                                        +{t.extra_days} gün ek ömür
                                                    </div>
                                                )}
                                            </div>
                                            {/* Checkbox göstergesi */}
                                            <div className={`size-8 rounded-lg flex items-center justify-center shrink-0 border-2 transition-all ${selected
                                                ? 'bg-green-500 dark:bg-green-600 border-green-500'
                                                : 'bg-white/60 dark:bg-slate-800/60 border-green-300 dark:border-green-700'}`}>
                                                {selected && <span className="material-symbols-outlined text-white text-lg">check</span>}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Footer — tek buton ile seçilenleri kurtar */}
                        <div className="sticky bottom-0 bg-slate-50 dark:bg-slate-800/50 px-4 py-3 border-t border-slate-200 dark:border-slate-700">
                            <button
                                onClick={handleBatchRescue}
                                disabled={rescueProcessing || rescueSelected.size === 0}
                                className="w-full py-3 rounded-2xl font-black text-white uppercase tracking-tight bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-lg active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {rescueProcessing ? (
                                    <>
                                        <span className="material-symbols-outlined animate-spin text-xl">progress_activity</span>
                                        Kurtarılıyor…
                                    </>
                                ) : (
                                    <>
                                        <span className="material-symbols-outlined text-xl">restaurant</span>
                                        {rescueSelected.size > 0 ? `Seçilenleri Kurtar (${rescueSelected.size})` : 'Tarif seçin'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* BREAKFAST MODALS */}
            {schoolLunchModal.isOpen && (
                <SchoolChildModal
                    isOpen={schoolLunchModal.isOpen}
                    onClose={() => setSchoolLunchModal({ isOpen: false, notifId: null, pendingAction: null })}
                    onSubmit={handleSchoolLunchSubmit}
                />
            )}

            {breakfastModalOpen && (
                <BreakfastRecommendationModal
                    isOpen={breakfastModalOpen}
                    onClose={() => setBreakfastModalOpen(false)}
                    onComplete={() => {
                        if (rawLatestBreakfastNotification?.id) {
                            setCompletedBreakfastNotificationId(rawLatestBreakfastNotification.id);
                            setNotifications((prev) => prev.map((notif) => (
                                notif.id === rawLatestBreakfastNotification.id
                                    ? { ...notif, isRead: true }
                                    : notif
                            )));
                            markAsRead(rawLatestBreakfastNotification.id);
                        }
                        setBreakfastModalOpen(false);
                        fetchNotifications();
                        setFeedbackModal({
                            isOpen: true,
                            title: 'KAHVALTI KAYDI TAMAMLANDI',
                            message: 'Afiyet olsun Şefim! Kahvaltı kaydını ve mutfak stoklarını birlikte güncelledim.',
                            type: 'success'
                        });
                    }}
                />
            )}

        </div >
    );
}
